"use client";

import React from "react";
import { MapPin, Phone, Clock, Mail, Loader, Check } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
const contactInfo = {
  address: "85 Great Portland Street, London, W1W 7LT",
  phone: "+44 (0) 20 7946 0123",
  email: "invest@yieldcapital.co.uk",
  hours: "Mon–Fri: 8am–6pm GMT"
};
const serviceOptions = ["Social Housing", "Supported Living", "Portfolio Diversification", "Tax-Efficient Investing", "Institutional Funds"];
export default function PlanYourVisit() {
  return <section data-section-id="32020" id="book-a-strategy-call" className="bg-background py-24 lg:py-32">
      <div className="container mx-auto px-4 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          {}
          <div className="flex flex-col justify-center space-y-12">
            <div>
              <h2 className="font-serif text-5xl md:text-6xl text-foreground mb-6 leading-tight">Contact Us</h2>
              <p className="font-default text-muted-foreground text-xl leading-relaxed max-w-xl">
                Secure your financial future with government-backed 10% yields. Schedule a consultation with our investment experts to discuss your portfolio objectives.
              </p>
            </div>

            <div className="space-y-8">
              <div className="flex items-start space-x-5 group">
                <div className="mt-1 text-primary transition-transform duration-300 group-hover:scale-110">
                  <MapPin className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-serif text-xl text-foreground mb-1">London Headquarters</h3>
                  <p className="font-default text-muted-foreground tracking-wide text-lg">15-17 Jockey's Fields, London WC1R 4BW</p>
                </div>
              </div>

              <div className="flex items-start space-x-5 group">
                <div className="mt-1 text-primary transition-transform duration-300 group-hover:scale-110">
                  <Phone className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-serif text-xl text-foreground mb-1">Direct Investor Line</h3>
                  <p className="font-default text-muted-foreground tracking-wide text-lg">+44 (0) 20 8142 6235</p>
                </div>
              </div>

              <div className="flex items-start space-x-5 group">
                <div className="mt-1 text-primary transition-transform duration-300 group-hover:scale-110">
                  <Mail className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-serif text-xl text-foreground mb-1">Investor Relations</h3>
                  <p className="font-default text-muted-foreground tracking-wide text-lg">buy@10percent-yield.com</p>
                </div>
              </div>

              <div className="flex items-start space-x-5 group">
                <div className="mt-1 text-primary transition-transform duration-300 group-hover:scale-110">
                  <Clock className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-serif text-xl text-foreground mb-1">Operating Hours</h3>
                  <p className="font-default text-muted-foreground tracking-wide text-lg">
                    {contactInfo.hours}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className="bg-card/50 backdrop-blur-sm border border-border/50 p-8 md:p-12 rounded-xl shadow-2xl">
            <FormProvider formKey="strategy_call_form_contact" formId="strategy_call_form_contact" sectionName="plan-your-visit" defaultValues={{
            full_name: "",
            email: "",
            phone: "",
            visit_date: "",
            guest_count: "100000",
            services: [],
            additional_requests: ""
          }} validationRules={{
            full_name: {
              required: "Full Name is required"
            },
            email: {
              required: "Email is required",
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: "Invalid email address"
              }
            },
            phone: {
              required: "Phone number is required",
              pattern: {
                value: /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/,
                message: "Invalid phone number"
              }
            },
            visit_date: {
              required: "Please select a preferred date"
            },
            guest_count: {
              required: "Investment capacity is required",
              min: {
                value: 50000,
                message: "Minimum investment £50,000"
              },
              max: {
                value: 10000000,
                message: "Contact for institutional rates"
              }
            }
          }} submitText="Schedule Call" successMessage="Your request has been received. An investment consultant will contact you within 24 hours.">
              <VisitForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}
function VisitForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="h-full flex flex-col items-center justify-center text-center p-12 border border-primary/20 bg-primary/5 rounded-xl animate-in fade-in zoom-in duration-500">
        <div className="w-20 h-20 bg-primary text-primary-foreground rounded-full flex items-center justify-center mb-8 shadow-xl">
          <Check className="w-10 h-10" />
        </div>
        <h3 className="font-serif text-3xl text-foreground mb-4">Request Confirmed</h3>
        <p className="font-default text-muted-foreground text-lg max-w-md">{successMessage}</p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-10">
      <div className="space-y-8">
        <FormField control={control} name="full_name" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-xs uppercase tracking-widest text-muted-foreground font-bold">Full Name</FormLabel>
              <FormControl>
                <input {...field} type="text" placeholder="e.g. Alexander Hamilton" className="w-full border-b border-border bg-transparent py-3 text-lg text-foreground placeholder:text-muted-foreground/30 outline-none transition-all duration-300 focus:border-primary focus:placeholder:text-muted-foreground/50" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <FormField control={control} name="email" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-default text-xs uppercase tracking-widest text-muted-foreground font-bold">Email Address</FormLabel>
                <FormControl>
                  <input {...field} type="email" placeholder="investor@example.com" className="w-full border-b border-border bg-transparent py-3 text-lg text-foreground placeholder:text-muted-foreground/30 outline-none transition-all duration-300 focus:border-primary focus:placeholder:text-muted-foreground/50" />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="phone" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-default text-xs uppercase tracking-widest text-muted-foreground font-bold">Phone Number</FormLabel>
                <FormControl>
                  <input {...field} type="tel" placeholder="+44 7700 900123" className="w-full border-b border-border bg-transparent py-3 text-lg text-foreground placeholder:text-muted-foreground/30 outline-none transition-all duration-300 focus:border-primary focus:placeholder:text-muted-foreground/50" />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <FormField control={control} name="visit_date" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-default text-xs uppercase tracking-widest text-muted-foreground font-bold">Preferred Call Date</FormLabel>
                <FormControl>
                  <input {...field} type="date" className="w-full border-b border-border bg-transparent py-3 text-lg text-foreground placeholder:text-muted-foreground/30 outline-none transition-all duration-300 focus:border-primary min-h-[52px]" />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="guest_count" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-default text-xs uppercase tracking-widest text-muted-foreground font-bold">Investment Capacity (£)</FormLabel>
                <FormControl>
                  <input {...field} type="number" min="50000" max="10000000" placeholder="100000" className="w-full border-b border-border bg-transparent py-3 text-lg text-foreground placeholder:text-muted-foreground/30 outline-none transition-all duration-300 focus:border-primary" />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <FormField control={control} name="services" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-xs uppercase tracking-widest text-muted-foreground font-bold block mb-4">Areas of Interest</FormLabel>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {serviceOptions.map((service, i) => <label key={service} data-index={i} className="flex items-center space-x-3 cursor-pointer group select-none">
                    <div className="relative flex items-center">
                      <input type="checkbox" value={service} checked={(field.value || []).includes(service)} onChange={e => {
                const checked = e.target.checked;
                const currentValues = field.value || [];
                if (checked) {
                  field.onChange([...currentValues, service]);
                } else {
                  field.onChange(currentValues.filter((v: string) => v !== service));
                }
              }} className="peer appearance-none w-5 h-5 border border-border rounded-md checked:bg-primary checked:border-primary transition-all cursor-pointer" />
                      <Check className="w-3.5 h-3.5 text-primary-foreground absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 opacity-0 peer-checked:opacity-100 pointer-events-none transition-opacity" />
                    </div>
                    <span className="font-default text-base text-foreground/80 group-hover:text-foreground transition-colors">{service}</span>
                  </label>)}
              </div>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="additional_requests" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-xs uppercase tracking-widest text-muted-foreground font-bold">Investment Objectives</FormLabel>
              <FormControl>
                <textarea {...field} rows={3} placeholder="Tell us about your portfolio goals or any specific questions..." className="w-full border-b border-border bg-transparent py-3 text-lg text-foreground placeholder:text-muted-foreground/30 outline-none transition-all duration-300 focus:border-primary resize-none" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-xl text-sm text-destructive flex items-center gap-2">
          <span className="font-bold">Error:</span> {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/90 rounded-xl h-16 uppercase tracking-widest text-sm font-default font-bold transition-all shadow-xl hover:shadow-2xl active:scale-[0.98]">
        {isSubmitting ? <>
            <Loader className="mr-2 h-5 w-5 animate-spin" />
            Scheduling Call...
          </> : "SCHEDULE CALL"}
      </Button>
    </Form>;
}